import React from "react";
import { ITEM_TYPES, fmtQty, stockOf } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";

export const selectCls = "flex h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm disabled:bg-slate-100";
export const emptyLine = () => ({ item_type: "fg", item_id: "", qty: "" });
export const linesValid = (lines) => lines.length > 0 && lines.every((l) => l.item_id && Number(l.qty) > 0);
export const optionalLinesValid = (lines) => lines.every((l) => l.item_id && Number(l.qty) > 0);

export const LineItemsEditor = ({
  lines, onChange, items, showStock = false, showReceived = false, testid = "lines",
  warehouse, minLines = 1, qtyLabel, allowNegative = false, extraCols = [],
}) => {
  const { t } = useLang();
  const update = (i, patch) => onChange(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden" data-testid={`${testid}-editor`}>
      <table className="min-w-full text-sm">
        <thead className="bg-slate-100/80 text-slate-700">
          <tr>
            <th className="text-left py-2 px-2 w-40">{t("kind")}</th>
            <th className="text-left py-2 px-2">{t("item")}</th>
            {showStock && <th className="text-right py-2 px-2 w-24">{t("stock")}</th>}
            {showReceived && <th className="text-right py-2 px-2 w-24">{t("received")}</th>}
            <th className="text-right py-2 px-2 w-28">{qtyLabel || t("qty")}</th>
            {extraCols.map((c) => <th key={c.header} className="text-right py-2 px-2 w-24">{c.header}</th>)}
            <th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => {
            const opts = items[l.item_type] || [];
            const sel = opts.find((o) => o.id === l.item_id);
            const locked = Number(l.received_qty || 0) > 0;
            return (
              <tr key={l.line_id || i} className="border-t border-slate-100">
                <td className="p-2">
                  <select className={selectCls} value={l.item_type} disabled={locked}
                          onChange={(e) => update(i, { item_type: e.target.value, item_id: "" })} data-testid={`${testid}-type-${i}`}>
                    {ITEM_TYPES.map((x) => <option key={x.v} value={x.v}>{t(x.labelKey)}</option>)}
                  </select>
                </td>
                <td className="p-2">
                  <select className={selectCls} value={l.item_id} disabled={locked}
                          onChange={(e) => update(i, { item_id: e.target.value })} data-testid={`${testid}-item-${i}`}>
                    <option value="">—</option>
                    {opts.map((o) => <option key={o.id} value={o.id}>{o.code} · {o.name}</option>)}
                  </select>
                </td>
                {showStock && <td className="p-2 text-right font-mono text-xs text-slate-600">{sel ? `${fmtQty(stockOf(sel, warehouse))} ${sel.unit}` : "-"}</td>}
                {showReceived && <td className="p-2 text-right font-mono text-xs text-emerald-700">{fmtQty(l.received_qty)}</td>}
                <td className="p-2">
                  <Input type="number" min={allowNegative ? undefined : "0"} className="h-9 text-right" value={l.qty}
                         onChange={(e) => update(i, { qty: e.target.value })} data-testid={`${testid}-qty-${i}`} />
                </td>
                {extraCols.map((c) => <td key={c.header} className="p-2 text-right font-mono text-xs">{c.render(l, sel)}</td>)}
                <td className="p-2">
                  <Button type="button" variant="ghost" size="sm" className="text-red-600" disabled={locked || lines.length <= minLines}
                          onClick={() => onChange(lines.filter((_, idx) => idx !== i))} data-testid={`${testid}-remove-${i}`}>
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="p-2 border-t border-slate-100 bg-slate-50">
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...lines, emptyLine()])} data-testid={`${testid}-add-line-btn`}>
          <Plus size={14} className="mr-1" />{t("add_line")}
        </Button>
      </div>
    </div>
  );
};
